/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import { useRef } from "react";
import * as React from "react";
import {
  buildExportTrackName,
  CompletedEnhanceSpeechTrack,
} from "components/EnhancedSpeech/Utilities";

import downloadEnhancedIconDark from "images/EnhanceSpeech/download-icon-dark.svg";
import downloadEnhancedIconDarkXL from "images/EnhanceSpeech/download-icon-dark-XL.svg";
import { webComponentShim } from "components/shared/WebComponentShim";
import { fetchExpiringUrl } from "components/EnhancedSpeech/Utilities";
import { ACCEPTED_FILE_TYPES } from "constants/AcceptedFiles";
import closeIcon from "images/close-icon-dark.svg";

export const ButtonAccented = styled(webComponentShim("sp-button")).attrs({
  treatment: "fill",
  variant: "accent",
  size: "l",
})``;

export const ButtonSecondaryOutline = styled(
  webComponentShim("sp-button")
).attrs({
  treatment: "outline",
  variant: "secondary",
  size: "l",
})``;

export const ButtonSecondaryXL = styled(webComponentShim("sp-button")).attrs({
  treatment: "fill",
  variant: "secondary",
  size: "xl",
})`
  flex-shrink: 0;
`;

export const UploadButtonLabel = styled.span`
  color: var(--spectrum-global-color-gray-900);
`;

export const DownloadIcon = styled.img.attrs({
  slot: "icon",
})`
  width: 20px;
  height: 20px;
`;

export const DownloadIconXL = styled(DownloadIcon)`
  width: 22px;
  height: 22px;
`;

export const DownloadTrackButton: React.FC<{
  disabled: boolean;
  onClick: VoidFunction;
}> = ({ disabled, onClick }) => {
  return (
    <overlay-trigger placement="bottom">
      <div slot="trigger">
        <ButtonSecondaryXL onClick={onClick} disabled={disabled || undefined}>
          {disabled && (
            <sp-progress-circle
              indeterminate
              size="s"
              slot="icon"
              aria-label={I18n.t("enhance_speech.downloading")}
            />
          )}
          {!disabled && <DownloadIconXL src={downloadEnhancedIconDarkXL} />}
          <span>
            {disabled
              ? I18n.t("enhance_speech.downloading")
              : I18n.t("enhance_speech.download")}
          </span>
        </ButtonSecondaryXL>
      </div>
      {disabled && (
        <sp-tooltip slot="hover-content" open>
          {I18n.t("enhance_speech.long_file_download_fyi")}
        </sp-tooltip>
      )}
    </overlay-trigger>
  );
};

export const BasicDownloadButton: React.FC<{
  track: CompletedEnhanceSpeechTrack;
}> = ({ track }) => {
  const enhancedFileName = buildExportTrackName(track);

  const downloadTrack = async (): Promise<void> => {
    const link = document.createElement("a");
    link.href = await fetchExpiringUrl(track.enhanceSpeechAudioUrl);
    // This may be overriden by the content-disposition header in the presigned URL
    link.download = enhancedFileName;
    link.click();
  };

  return (
    <ButtonSecondaryXL onClick={downloadTrack}>
      <DownloadIcon src={downloadEnhancedIconDark} />
      <span>{I18n.t("enhance_speech.download")}</span>
    </ButtonSecondaryXL>
  );
};

// TODO: Extract upload button to use in canvas action and music collection
export const UploadButton: React.FC<{
  onFilesSelected: (files: FileList) => void;
  title: string;
}> = ({ onFilesSelected, title }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    fileInputRef.current?.click();
    event.stopPropagation();
  };

  const onFileChanged = (): void => {
    const selectedFiles = fileInputRef.current?.files;
    if (!selectedFiles) {
      return;
    }
    onFilesSelected(selectedFiles);
  };

  return (
    <ButtonSecondaryOutline onClick={onClick}>
      <UploadButtonLabel>{title}</UploadButtonLabel>
      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileChanged}
        hidden
        id="enhance-file-upload"
        accept={ACCEPTED_FILE_TYPES}
        multiple={true}
        aria-label={I18n.t("project_show.insert_clip")}
      />
    </ButtonSecondaryOutline>
  );
};

export const CloseButton = styled.button`
  background-color: transparent;
  border: none;
  padding: 0;
  height: 22px;
  width: 22px;
  cursor: pointer;
  background-image: url("${closeIcon}");
  background-repeat: no-repeat;
  background-position: center;
`;
